import { useState } from 'react';
import { useAuth } from '@/lib/use-auth';
import { ApiError } from '@/lib/api';
import { giveConsent, fileDispute, fetchSubstitutes } from '@/lib/booking-api';
import type { BookingResponse } from '@/lib/schemas/booking';
import type { PublicTutorView } from '@/lib/schemas/tutor-profile';
import type { ChildResponse } from '@/lib/schemas/parent-profile';
import { ReviewModal } from './ReviewModal';
import { ChatPanel } from '@/components/ChatPanel';
import type { PublicTutorCard } from '@/lib/schemas/tutor-profile';
import { X, Video, Calendar, Clock, MessageSquare, Check } from 'lucide-react';

interface BookingDetailPanelProps {
  booking: BookingResponse;
  tutor: PublicTutorView | undefined;
  child: ChildResponse | undefined;
  onClose: () => void;
  onUpdate: (updated: BookingResponse) => void;
}

const STATUS_LABELS: Record<string, string> = {
  pending: 'Pending', accepted: 'Accepted', declined: 'Declined', cancelled: 'Cancelled', completed: 'Completed',
};

const STATUS_COLORS: Record<string, string> = {
  pending: 'bg-amber-50 text-amber-700 border border-amber-200/50 dark:bg-amber-950/20 dark:text-amber-400 dark:border-amber-900/30',
  accepted: 'bg-emerald-50 text-emerald-700 border border-emerald-200/50 dark:bg-emerald-950/20 dark:text-emerald-400 dark:border-emerald-900/30',
  declined: 'bg-rose-50 text-rose-700 border border-rose-200/50 dark:bg-rose-950/20 dark:text-rose-400 dark:border-rose-900/30',
  cancelled: 'bg-slate-50 text-slate-500 border border-slate-200/50 dark:bg-slate-800/40 dark:text-slate-400 dark:border-slate-800/30',
  completed: 'bg-blue-50 text-blue-700 border border-blue-200/50 dark:bg-blue-950/20 dark:text-blue-400 dark:border-blue-900/30',
};

function formatDate(isoStart: string): string {
  const start = new Date(isoStart);
  return start.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' });
}

function formatTime(isoStart: string, isoEnd: string): string {
  const start = new Date(isoStart);
  const end = new Date(isoEnd);
  const startTime = start.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' });
  const endTime = end.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' });
  return `${startTime} – ${endTime}`;
}

export function BookingDetailPanel({ booking: b, tutor, child, onClose, onUpdate }: BookingDetailPanelProps) {
  const { accessToken } = useAuth();
  const [actionError, setActionError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [disputeOpen, setDisputeOpen] = useState(false);
  const [disputeReason, setDisputeReason] = useState('');
  const [reviewOpen, setReviewOpen] = useState(false);
  const [reviewDone, setReviewDone] = useState(false);
  const [substitutes, setSubstitutes] = useState<PublicTutorCard[] | null>(null);
  const [substitutesLoading, setSubstitutesLoading] = useState(false);
  const [chatOpen, setChatOpen] = useState(false);

  async function handleConsent() {
    if (!accessToken) return;
    setBusy(true);
    setActionError(null);
    try {
      const updated = await giveConsent(accessToken, b.id);
      onUpdate(updated);
    } catch (e) {
      setActionError(e instanceof ApiError ? e.message : 'Failed to give consent');
    } finally {
      setBusy(false);
    }
  }

  async function handleFileDispute() {
    if (!accessToken || !disputeReason.trim()) return;
    setBusy(true);
    setActionError(null);
    try {
      await fileDispute(accessToken, { bookingId: b.id, reason: disputeReason.trim() });
      setDisputeOpen(false);
      setDisputeReason('');
    } catch (e) {
      setActionError(e instanceof ApiError ? e.message : 'Failed to file dispute');
    } finally {
      setBusy(false);
    }
  }

  const dateStr = formatDate(b.slotStartUtc);
  const timeStr = formatTime(b.slotStartUtc, b.slotEndUtc);

  return (
    <div className="fixed right-0 top-0 bottom-0 w-full sm:w-[450px] bg-white/95 dark:bg-slate-900/95 backdrop-blur-md border-l border-slate-200 dark:border-slate-800 shadow-2xl overflow-y-auto z-50 flex flex-col transition-all duration-300">
      {/* Header */}
      <div className="sticky top-0 bg-white/80 dark:bg-slate-900/80 backdrop-blur-md border-b border-slate-100 dark:border-slate-800/85 p-4 flex items-center justify-between z-20">
        <div className="flex items-center gap-3">
          <div className="h-10 w-10 rounded-xl bg-gradient-to-br from-blue-500 to-indigo-600 text-white flex items-center justify-center font-extrabold shadow-sm">
            {tutor?.displayName ? tutor.displayName.charAt(0).toUpperCase() : 'B'}
          </div>
          <div>
            <h3 className="font-extrabold text-slate-900 dark:text-white leading-tight text-sm">
              {tutor?.displayName ?? 'Booking'}
            </h3>
            <p className="text-[9px] font-bold text-slate-400 dark:text-slate-500 uppercase tracking-widest mt-0.5">
              Class Details
            </p>
          </div>
        </div>
        <button
          onClick={onClose}
          className="h-8 w-8 rounded-full flex items-center justify-center bg-slate-50 hover:bg-slate-100 dark:bg-slate-800 dark:hover:bg-slate-700 transition-colors cursor-pointer border border-slate-100/50 dark:border-slate-700/50"
        >
          <X size={16} className="text-slate-600 dark:text-slate-400" />
        </button>
      </div>

      {/* Content */}
      <div className="p-5 space-y-6 flex-1">
        {/* Status */}
        <div className="flex items-center justify-between bg-slate-50/50 dark:bg-slate-800/25 p-3 rounded-2xl border border-slate-100 dark:border-slate-800/60">
          <span className="text-xs font-bold uppercase tracking-wider text-slate-500 dark:text-slate-400">Status</span>
          <span className={`inline-flex rounded-full px-3 py-0.5 text-xs font-bold ${STATUS_COLORS[b.status] ?? ''}`}>
            {STATUS_LABELS[b.status] ?? b.status}
          </span>
        </div>

        {/* Info Grid Card */}
        <div className="bg-slate-50/40 dark:bg-slate-800/20 p-4.5 rounded-2xl border border-slate-200/50 dark:border-slate-800/40 space-y-4 shadow-xs">
          {/* Date & Time */}
          <div className="space-y-2.5">
            <div className="flex items-center gap-2.5 text-xs font-bold text-slate-600 dark:text-slate-350">
              <Calendar className="w-4 h-4 text-blue-500 shrink-0" />
              <span>{dateStr}</span>
            </div>
            <div className="flex items-center gap-2.5 text-xs font-bold text-slate-600 dark:text-slate-350">
              <Clock className="w-4 h-4 text-violet-500 shrink-0" />
              <span>{timeStr}</span>
            </div>
          </div>

          <div className="h-px bg-slate-200/60 dark:bg-slate-800/60" />

          {/* Child */}
          {child && (
            <div className="space-y-1">
              <p className="text-[10px] font-bold text-slate-400 dark:text-slate-500 uppercase tracking-wider">Learner</p>
              <p className="text-xs font-extrabold text-slate-800 dark:text-slate-200">{child.name} <span className="font-semibold text-slate-500 dark:text-slate-400">(Grade {child.grade})</span></p>
            </div>
          )}

          {/* Subject */}
          {b.subject && (
            <div className="space-y-1">
              <p className="text-[10px] font-bold text-slate-400 dark:text-slate-500 uppercase tracking-wider">Subject</p>
              <p className="text-xs font-extrabold text-slate-800 dark:text-slate-200">{b.subject}</p>
            </div>
          )}
        </div>

        {/* Notes */}
        {b.parentNotes && (
          <div className="space-y-1.5">
            <p className="text-[10px] font-bold text-slate-400 dark:text-slate-500 uppercase tracking-wider">Parent Notes</p>
            <p className="text-xs italic text-slate-600 dark:text-slate-350 bg-slate-50/50 dark:bg-slate-800/40 p-3 rounded-xl border-l-2 border-brand-500 leading-relaxed dark:border-brand-500">{b.parentNotes}</p>
          </div>
        )}

        {/* Actions */}
        <div className="border-t border-slate-100 dark:border-slate-800 pt-5 space-y-2.5">
          {actionError && <p className="text-xs font-semibold text-red-650 dark:text-red-400 mt-2">{actionError}</p>}

          {b.status === 'accepted' && (
            <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2 w-full">
              {b.videoLink && (
                <a
                  href={b.videoLink}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex-1 inline-flex items-center justify-center gap-1 rounded-xl bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 px-2.5 py-2.5 text-[11px] font-extrabold uppercase tracking-wider text-white hover:shadow-md hover:shadow-blue-500/10 transition-all cursor-pointer active:scale-95 duration-200 whitespace-nowrap"
                >
                  <Video className="w-3.5 h-3.5 shrink-0" />
                  <span>Join Class</span>
                </a>
              )}

              <button
                onClick={() => setChatOpen(!chatOpen)}
                className="flex-1 inline-flex items-center justify-center gap-1 rounded-xl bg-indigo-50 hover:bg-indigo-100/80 text-indigo-700 border border-indigo-200/60 dark:bg-indigo-950/30 dark:hover:bg-indigo-950/50 dark:text-indigo-300 dark:border-indigo-900/50 px-2.5 py-2.5 text-[11px] font-extrabold uppercase tracking-wider transition-all cursor-pointer active:scale-95 duration-200 whitespace-nowrap"
              >
                <MessageSquare className="w-3.5 h-3.5 shrink-0" />
                <span>Chat</span>
              </button>

              <button
                onClick={() => void handleConsent()}
                disabled={busy}
                className="flex-1 inline-flex items-center justify-center gap-1 rounded-xl bg-gradient-to-r from-emerald-600 to-teal-600 hover:from-emerald-700 hover:to-teal-700 px-2.5 py-2.5 text-[11px] font-extrabold uppercase tracking-wider text-white disabled:opacity-50 hover:shadow-md hover:shadow-emerald-500/10 transition-all cursor-pointer active:scale-95 duration-200 whitespace-nowrap"
              >
                <Check className="w-3.5 h-3.5 shrink-0" />
                <span>Give Consent</span>
              </button>
            </div>
          )}

          {(b.status === 'declined' || b.status === 'cancelled') && (
            <button
              onClick={() => {
                setSubstitutesLoading(true);
                if (accessToken) {
                  fetchSubstitutes(accessToken, b.id)
                    .then((data) => setSubstitutes(data))
                    .catch(() => setSubstitutes([]))
                    .finally(() => setSubstitutesLoading(false));
                }
              }}
              disabled={substitutesLoading}
              className="w-full rounded-xl bg-brand-600 px-4 py-2.5 text-xs font-bold text-white hover:bg-brand-700 disabled:opacity-50 transition-all shadow-sm shadow-brand-500/10 cursor-pointer active:scale-95 duration-200"
            >
              {substitutesLoading ? 'Searching…' : 'Find Substitute'}
            </button>
          )}

          {b.status === 'completed' && !disputeOpen && !reviewDone && (
            <>
              <button
                onClick={() => setReviewOpen(true)}
                className="w-full rounded-xl bg-amber-550 hover:bg-amber-600 px-4 py-2.5 text-xs font-bold text-white transition-all shadow-sm shadow-amber-500/10 cursor-pointer active:scale-95 duration-200"
              >
                Leave Review
              </button>
              <button
                onClick={() => setDisputeOpen(true)}
                className="w-full rounded-xl border border-red-200 bg-white px-4 py-2.5 text-xs font-bold text-red-650 hover:bg-red-50 dark:border-red-900/50 dark:bg-slate-800 dark:text-red-400 dark:hover:bg-red-950/30 transition-all cursor-pointer"
              >
                File Dispute
              </button>
            </>
          )}

          {b.status === 'completed' && reviewDone && (
            <p className="text-xs text-green-600 dark:text-green-400 text-center font-bold">✓ Review submitted</p>
          )}

          {b.status === 'completed' && disputeOpen && (
            <div className="space-y-2.5">
              <textarea
                value={disputeReason}
                onChange={(e) => setDisputeReason(e.target.value)}
                placeholder="Describe the issue…"
                rows={3}
                className="w-full rounded-xl border border-slate-200 bg-white px-3 py-2 text-xs text-slate-900 focus:outline-none focus:ring-2 focus:ring-blue-500 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-100 dark:focus:ring-blue-500"
              />
              <button
                onClick={() => void handleFileDispute()}
                disabled={busy || !disputeReason.trim()}
                className="w-full rounded-xl bg-red-600 px-4 py-2.5 text-xs font-bold text-white hover:bg-red-700 disabled:opacity-50 transition-all cursor-pointer active:scale-95 duration-200"
              >
                {busy ? 'Submitting…' : 'Submit Dispute'}
              </button>
              <button
                onClick={() => { setDisputeOpen(false); setDisputeReason(''); }}
                className="w-full rounded-xl border border-slate-200 bg-white px-4 py-2.5 text-xs font-bold text-slate-600 hover:bg-slate-50 dark:border-slate-700 dark:bg-slate-800 dark:text-slate-350 dark:hover:bg-slate-700 transition-all cursor-pointer"
              >
                Cancel
              </button>
            </div>
          )}
        </div>

        {/* Substitutes */}
        {substitutes !== null && (
          <div className="border-t border-slate-100 dark:border-slate-800 pt-5">
            <p className="mb-2.5 text-xs font-bold text-slate-500 dark:text-slate-400 uppercase tracking-wider">
              {substitutes.length === 0 ? 'No substitutes found.' : `${substitutes.length} substitute${substitutes.length === 1 ? '' : 's'} available:`}
            </p>
            <ul className="space-y-2">
              {substitutes.map((t) => (
                <li key={t.id} className="flex items-center justify-between rounded-xl border border-slate-200/60 bg-white/60 px-3.5 py-2.5 text-xs dark:border-slate-800/60 dark:bg-slate-900/40 hover:bg-slate-50/50 dark:hover:bg-slate-800/30 transition-all duration-200">
                  <span className="font-bold text-slate-800 dark:text-slate-100">{t.displayName}</span>
                  <a href={`/tutors/${t.id}`} className="text-xs font-bold text-blue-600 hover:text-blue-700 dark:text-blue-400 dark:hover:text-blue-300">
                    View Profile →
                  </a>
                </li>
              ))}
            </ul>
          </div>
        )}

        {/* Chat */}
        {chatOpen && b.status === 'accepted' && accessToken && (
          <div className="border-t border-slate-100 dark:border-slate-800 pt-5">
            <ChatPanel bookingId={b.id} accessToken={accessToken} open={chatOpen} onOpenChange={setChatOpen} />
          </div>
        )}

        {/* Modals */}
        {reviewOpen && accessToken && (
          <ReviewModal
            bookingId={b.id}
            accessToken={accessToken}
            onDone={() => { setReviewOpen(false); setReviewDone(true); }}
            onClose={() => setReviewOpen(false)}
          />
        )}
      </div>
    </div>
  );
}
